import { Send } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { AbandonedCheckout } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';

import { OrderLink } from '@/features/orders';

import { emailSentState, recoveryState, unsendableHint } from '../schemas/checkout-rules';

export interface AbandonedCheckoutColumnsOptions {
  storeId: string;
  canWrite: boolean;
  /** Whether the store's recovery email is switched on; null when the member cannot tell. */
  templateEnabled: boolean | null;
  sendingId: string | null;
  onSend: (checkout: AbandonedCheckout) => void;
}

/** Column model of the abandoned checkouts table; `meta` ties columns to the list's sorts and filters. */
export function useAbandonedCheckoutColumns({
  storeId,
  canWrite,
  templateEnabled,
  sendingId,
  onSend,
}: AbandonedCheckoutColumnsOptions) {
  const { t } = useTranslation('abandonedCheckouts');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<AbandonedCheckout>>>(
    () => [
      {
        id: 'email',
        header: t('columns.email'),
        cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
        meta: { label: t('columns.email'), lockVisibility: true },
      },
      {
        id: 'total',
        header: t('columns.total'),
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">{format.money(row.original.total)}</span>
        ),
        meta: { sortField: 'total', filterKey: 'total', align: 'end' },
        size: 120,
      },
      {
        id: 'items',
        header: t('columns.items'),
        cell: ({ row }) => (
          <span className="tabular-nums">{t('itemCount', { count: row.original.lineCount })}</span>
        ),
        meta: { align: 'end' },
        size: 90,
      },
      {
        id: 'lastActivityAt',
        header: t('columns.lastActivity'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.lastActivityAt)}>
            {format.relative(row.original.lastActivityAt)}
          </span>
        ),
        meta: { sortField: 'lastActivityAt', filterKey: 'lastActivityAt' },
        size: 130,
      },
      {
        id: 'recoveryEmailSentAt',
        header: t('columns.emailSent'),
        cell: ({ row }) => {
          const state = emailSentState(row.original, templateEnabled);
          if (state === 'sent' && row.original.recoveryEmailSentAt) {
            return (
              <span
                className="text-muted-foreground"
                title={format.utc(row.original.recoveryEmailSentAt)}
              >
                {format.relative(row.original.recoveryEmailSentAt)}
              </span>
            );
          }
          return (
            <span className="text-muted-foreground">
              {state === 'template_off' ? t('templateOff') : t('notSent')}
            </span>
          );
        },
        meta: {
          label: t('columns.emailSent'),
          filterKey: 'recoveryEmailSent',
          tour: 'abandoned.list.send',
        },
        size: 130,
      },
      {
        id: 'recovered',
        header: t('columns.recovered'),
        cell: ({ row }) => {
          const state = recoveryState(row.original);
          if (state === 'recovered' && row.original.recoveredOrderId) {
            return (
              <OrderLink
                storeId={storeId}
                orderId={row.original.recoveredOrderId}
                name={row.original.recoveredOrderName}
              />
            );
          }
          return (
            <Badge
              variant="outline"
              className={state === 'expired' ? 'border-border text-muted-foreground' : undefined}
            >
              {t(`state.${state}`)}
            </Badge>
          );
        },
        meta: { label: t('columns.recovered'), filterKey: 'recovered' },
        size: 120,
      },
      ...(canWrite
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">{t('columns.actions')}</span>,
              cell: ({ row }: { row: { original: AbandonedCheckout } }) => {
                if (row.original.canSendRecovery) {
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={sendingId === row.original.id}
                      aria-label={t('recovery.sendTo', { email: row.original.email })}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSend(row.original);
                      }}
                    >
                      <Send aria-hidden />
                      {t('recovery.send')}
                    </Button>
                  );
                }
                const hint = unsendableHint(row.original, templateEnabled);
                return hint ? (
                  <span className="text-muted-foreground text-xs">{t(`recovery.${hint}`)}</span>
                ) : null;
              },
              meta: { label: t('columns.actions') },
              size: 170,
            },
          ]
        : []),
    ],
    [canWrite, format, onSend, sendingId, storeId, t, templateEnabled],
  );
}
